import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../helper/cropImage';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { toast } from 'react-toastify';

const ImageUpload = ({ forceCrop = false, onUploadComplete, originalFileName }) => {
    const [fileName, setFileName] = useState('');
    const [imageSrc, setImageSrc] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const api = useApiServices();

    const uploadFile = async (fileBlob, name) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', fileBlob, name);
        const { isSuccess, result } = await api.UploadFileAsync(formData);
        setUploading(false);
        if (isSuccess) {
            setUploadedFile(result);
            setImageSrc(null);
            if (onUploadComplete) onUploadComplete(result);
        } else {
            toast.error('อัปโหลดไม่สำเร็จ');
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file?.type.startsWith('image/')) {
            toast.error('เฉพาะไฟล์รูปภาพเท่านั้น');
            return;
        }
        setFileName(file.name);
        if (!forceCrop) {
            uploadFile(file, file.name);
        } else {
            const reader = new FileReader();
            reader.onload = () => setImageSrc(reader.result);
            reader.readAsDataURL(file);
        }
    }, [forceCrop]);

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCropConfirm = useCallback(async () => {
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            await uploadFile(croppedBlob, fileName);
        } catch (e) {
            toast.error('ครอปรูปไม่สำเร็จ');
        }
    }, [imageSrc, croppedAreaPixels, fileName]);

    const handleReset = () => {
        setImageSrc(null);
        setUploadedFile(null);
        setFileName('');
        if (onUploadComplete) onUploadComplete(null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected: () => toast.error('กรุณาอัปโหลดเฉพาะไฟล์รูปภาพเท่านั้น'),
        accept: { 'image/*': [] },
    });

    /* ── Crop UI ── */
    if (imageSrc) return (
        <div>
            <div style={{ position: 'relative', width: '100%', height: 320, background: '#0f172a', borderRadius: 10, overflow: 'hidden' }}>
                <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                />
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button
                    type="button"
                    onClick={handleCropConfirm}
                    style={{
                        flex: 1, padding: '8px 0',
                        background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)',
                        border: 'none', borderRadius: 8,
                        color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                >
                    <i className="fa fa-check" style={{ marginRight: 6 }} />ยืนยัน
                </button>
                <button
                    type="button"
                    onClick={handleReset}
                    style={{
                        padding: '8px 16px', background: '#f3f4f6',
                        border: '1px solid #e5e7eb', borderRadius: 8,
                        color: '#374151', fontSize: 13, cursor: 'pointer',
                    }}
                >
                    ยกเลิก
                </button>
            </div>
        </div>
    );

    /* ── Dropzone ── */
    return (
        <div
            {...getRootProps()}
            style={{
                border: `2px dashed ${isDragActive ? '#4f46e5' : uploadedFile ? '#22c55e' : '#d1d5db'}`,
                borderRadius: 10, padding: '20px 16px', textAlign: 'center',
                background: isDragActive ? '#eef2ff' : uploadedFile ? '#f0fdf4' : '#fafafa',
                cursor: uploading ? 'wait' : 'pointer',
                transition: 'all 0.15s',
            }}
        >
            <input {...getInputProps()} />

            {uploading ? (
                <div style={{ color: '#6b7280', fontSize: 13 }}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: 20, marginBottom: 6, display: 'block', color: '#4f46e5' }} />
                    กำลังอัปโหลด...
                </div>
            ) : uploadedFile ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 4,
                    }}>
                        <i className="fa fa-check" style={{ color: '#16a34a', fontSize: 16 }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>อัปโหลดแล้ว</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{uploadedFile.originalFileName || originalFileName || 'ไฟล์'}</span>
                    <span style={{ fontSize: 11, color: '#4f46e5', marginTop: 2 }}>คลิกเพื่อเปลี่ยน</span>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: isDragActive ? '#e0e7ff' : '#f3f4f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 4,
                    }}>
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: 18, color: isDragActive ? '#4f46e5' : '#9ca3af' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: isDragActive ? '#4f46e5' : '#374151' }}>
                        {isDragActive ? 'วางไฟล์ที่นี่' : 'คลิกหรือลากไฟล์มาวาง'}
                    </span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>รองรับ JPG, PNG, WEBP</span>
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
